/**
 * TermsModal Component
 * Modal de Termos de Uso e Politica de Privacidade (LGPD)
 * Exibe conteudo juridico em abas com scroll
 */

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';

interface TermsModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function TermsModal({ open, onOpenChange }: TermsModalProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-full max-w-[calc(100vw-2rem)] sm:max-w-2xl max-h-[85vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>Termos de Uso e Politica de Privacidade</DialogTitle>
        </DialogHeader>

        <Tabs defaultValue="terms" className="flex-1 flex flex-col min-h-0">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="terms">Termos de Uso</TabsTrigger>
            <TabsTrigger value="privacy">Politica de Privacidade</TabsTrigger>
          </TabsList>

          <TabsContent value="terms" className="flex-1 min-h-0 mt-4">
            <ScrollArea className="h-[50vh] pr-4">
              <div className="space-y-4 text-sm text-muted-foreground">
                <p className="text-xs text-muted-foreground/70">
                  Ultima atualizacao: Janeiro de 2026
                </p>
                <TermsOfUseContent />
              </div>
            </ScrollArea>
          </TabsContent>

          <TabsContent value="privacy" className="flex-1 min-h-0 mt-4">
            <ScrollArea className="h-[50vh] pr-4">
              <div className="space-y-4 text-sm text-muted-foreground">
                <p className="text-xs text-muted-foreground/70">
                  Ultima atualizacao: Janeiro de 2026
                </p>
                <PrivacyPolicyContent />
              </div>
            </ScrollArea>
          </TabsContent>
        </Tabs>

        <DialogFooter className="flex justify-end pt-4 border-t">
          <Button onClick={() => onOpenChange(false)}>Entendi</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function TermsOfUseContent() {
  return (
    <>
      <section>
        <h3 className="font-medium text-foreground mb-2 text-sm">1. ACEITACAO DOS TERMOS</h3>
        <p>
          Ao acessar e utilizar nossa plataforma de diagnostico empresarial, voce concorda com estes
          Termos de Uso. Se voce nao concordar com qualquer parte destes termos, nao utilize nossos
          servicos.
        </p>
      </section>

      <section>
        <h3 className="font-medium text-foreground mb-2 text-sm">2. DESCRICAO DO SERVICO</h3>
        <p>
          Nossa plataforma oferece reviews conversacionais baseados em inteligencia artificial
          para avaliar a maturidade de governanca e performance da sua empresa. Os diagnosticos sao
          gerados com base nas suas respostas e fornecem insights personalizados para identificar
          gargalos operacionais e oportunidades de melhoria.
        </p>
      </section>

      <section>
        <h3 className="font-medium text-foreground mb-2 text-sm">3. CADASTRO E INFORMACOES</h3>
        <p>Para utilizar nossos servicos, voce deve fornecer:</p>
        <ul className="list-disc pl-5 mt-2 space-y-1">
          <li>Nome completo</li>
          <li>Endereco de e-mail valido</li>
        </ul>
        <p className="mt-2">
          Voce e responsavel pela veracidade das informacoes fornecidas e pela confidencialidade dos
          seus dados de acesso.
        </p>
      </section>

      <section>
        <h3 className="font-medium text-foreground mb-2 text-sm">4. USO ADEQUADO</h3>
        <p>Voce concorda em:</p>
        <ul className="list-disc pl-5 mt-2 space-y-1">
          <li>Fornecer informacoes verdadeiras durante os reviews</li>
          <li>Nao utilizar o servico para fins ilegais ou nao autorizados</li>
          <li>Nao tentar acessar areas restritas do sistema</li>
          <li>Nao reproduzir ou distribuir o conteudo sem autorizacao</li>
        </ul>
      </section>

      <section>
        <h3 className="font-medium text-foreground mb-2 text-sm">5. PROPRIEDADE INTELECTUAL</h3>
        <p>
          Todo o conteudo da plataforma, incluindo metodologias de review, textos, graficos,
          logos e software, sao de nossa propriedade ou licenciados para nos, protegidos por leis de
          propriedade intelectual brasileiras e internacionais.
        </p>
      </section>

      <section>
        <h3 className="font-medium text-foreground mb-2 text-sm">6. RESULTADOS DOS REVIEWS</h3>
        <p>
          Os resultados e recomendacoes gerados sao baseados nas informacoes fornecidas por voce e
          tem carater orientativo. Nao nos responsabilizamos por decisoes empresariais tomadas com
          base exclusiva nos diagnosticos. Recomendamos que consulte profissionais especializados
          antes de implementar mudancas significativas.
        </p>
      </section>

      <section>
        <h3 className="font-medium text-foreground mb-2 text-sm">
          7. LIMITACAO DE RESPONSABILIDADE
        </h3>
        <p>
          Nao garantimos que o servico sera ininterrupto ou livre de erros. Em nenhum caso seremos
          responsaveis por danos indiretos, incidentais, especiais ou consequentes decorrentes do
          uso ou impossibilidade de uso da plataforma.
        </p>
      </section>

      <section>
        <h3 className="font-medium text-foreground mb-2 text-sm">8. MODIFICACOES</h3>
        <p>
          Reservamo-nos o direito de modificar estes termos a qualquer momento. Alteracoes
          significativas serao comunicadas por e-mail ou atraves de aviso na plataforma com
          antecedencia minima de 30 dias.
        </p>
      </section>

      <section>
        <h3 className="font-medium text-foreground mb-2 text-sm">9. RESCISAO</h3>
        <p>
          Podemos suspender ou encerrar seu acesso em caso de violacao destes termos, sem aviso
          previo. Voce tambem pode encerrar sua conta a qualquer momento entrando em contato
          conosco.
        </p>
      </section>

      <section>
        <h3 className="font-medium text-foreground mb-2 text-sm">10. LEI APLICAVEL E FORO</h3>
        <p>
          Estes termos sao regidos pelas leis da Republica Federativa do Brasil. Fica eleito o foro
          da comarca de Sao Paulo/SP para dirimir quaisquer questoes decorrentes deste documento,
          com renuncia a qualquer outro, por mais privilegiado que seja.
        </p>
      </section>

      <section>
        <h3 className="font-medium text-foreground mb-2 text-sm">11. CONTATO</h3>
        <p>
          Para duvidas sobre estes termos, entre em contato atraves do e-mail:
          juridico@bossabox.com
        </p>
      </section>
    </>
  );
}

function PrivacyPolicyContent() {
  return (
    <>
      <p>
        Esta Politica de Privacidade descreve como coletamos, usamos e protegemos suas informacoes
        pessoais, em conformidade com a Lei Geral de Protecao de Dados (Lei n 13.709/2018 - LGPD).
      </p>

      <section>
        <h3 className="font-medium text-foreground mb-2 text-sm">1. CONTROLADOR DOS DADOS</h3>
        <p>
          A empresa responsavel pelo tratamento dos seus dados pessoais e identificada na
          plataforma. Para contato com nosso Encarregado de Protecao de Dados (DPO), utilize:
          juridico@bossabox.com
        </p>
      </section>

      <section>
        <h3 className="font-medium text-foreground mb-2 text-sm">2. DADOS QUE COLETAMOS</h3>
        <p className="font-medium text-foreground/80">2.1 Dados fornecidos por voce:</p>
        <ul className="list-disc pl-5 mt-2 space-y-1">
          <li>Nome completo</li>
          <li>Endereco de e-mail</li>
          <li>Respostas aos reviews de governanca e performance</li>
        </ul>
        <p className="font-medium text-foreground/80 mt-3">2.2 Dados coletados automaticamente:</p>
        <ul className="list-disc pl-5 mt-2 space-y-1">
          <li>Endereco IP</li>
          <li>Tipo de navegador e dispositivo</li>
          <li>Data e horario de acesso</li>
          <li>Paginas visitadas e tempo de permanencia</li>
        </ul>
      </section>

      <section>
        <h3 className="font-medium text-foreground mb-2 text-sm">3. FINALIDADE DO TRATAMENTO</h3>
        <p>Utilizamos seus dados para:</p>
        <ul className="list-disc pl-5 mt-2 space-y-1">
          <li>Realizar os diagnosticos de governanca e performance</li>
          <li>Gerar relatorios personalizados com base nas suas respostas</li>
          <li>Enviar os resultados do review por e-mail</li>
          <li>Melhorar nossos servicos e experiencia do usuario</li>
          <li>Comunicar atualizacoes relevantes sobre a plataforma</li>
          <li>Cumprir obrigacoes legais e regulatorias</li>
        </ul>
      </section>

      <section>
        <h3 className="font-medium text-foreground mb-2 text-sm">4. BASE LEGAL (Art. 7 LGPD)</h3>
        <p>O tratamento dos seus dados pessoais e realizado com base em:</p>
        <ul className="list-disc pl-5 mt-2 space-y-1">
          <li>Consentimento (Art. 7, I): para coleta de dados e envio de comunicacoes</li>
          <li>Execucao de contrato (Art. 7, V): para prestacao do servico de review</li>
          <li>
            Legitimo interesse (Art. 7, IX): para melhorias do servico e seguranca da plataforma
          </li>
        </ul>
      </section>

      <section>
        <h3 className="font-medium text-foreground mb-2 text-sm">5. COMPARTILHAMENTO DE DADOS</h3>
        <p>Seus dados podem ser compartilhados com:</p>
        <ul className="list-disc pl-5 mt-2 space-y-1">
          <li>Prestadores de servicos essenciais (hospedagem, e-mail, analytics)</li>
          <li>Autoridades publicas, quando exigido por lei ou ordem judicial</li>
        </ul>
        <p className="mt-2">
          Nao vendemos, alugamos ou comercializamos seus dados pessoais com terceiros para fins de
          marketing.
        </p>
      </section>

      <section>
        <h3 className="font-medium text-foreground mb-2 text-sm">6. TRANSFERENCIA INTERNACIONAL</h3>
        <p>
          Alguns dos nossos prestadores de servicos podem estar localizados fora do Brasil. Nestes
          casos, garantimos que a transferencia ocorra em conformidade com a LGPD, mediante
          clausulas contratuais padrao ou outros mecanismos legalmente previstos.
        </p>
      </section>

      <section>
        <h3 className="font-medium text-foreground mb-2 text-sm">7. RETENCAO DOS DADOS</h3>
        <p>Seus dados serao mantidos pelo periodo necessario para:</p>
        <ul className="list-disc pl-5 mt-2 space-y-1">
          <li>Prestacao dos servicos contratados</li>
          <li>Cumprimento de obrigacoes legais (minimo de 5 anos para registros fiscais)</li>
          <li>Exercicio regular de direitos em processos judiciais ou administrativos</li>
        </ul>
        <p className="mt-2">
          Apos o termino da finalidade, os dados serao eliminados de forma segura ou anonimizados
          para fins estatisticos.
        </p>
      </section>

      <section>
        <h3 className="font-medium text-foreground mb-2 text-sm">
          8. SEUS DIREITOS (Art. 18 LGPD)
        </h3>
        <p>Voce tem direito a:</p>
        <ul className="list-disc pl-5 mt-2 space-y-1">
          <li>Confirmar a existencia de tratamento dos seus dados</li>
          <li>Acessar seus dados pessoais</li>
          <li>Corrigir dados incompletos, inexatos ou desatualizados</li>
          <li>
            Solicitar anonimizacao, bloqueio ou eliminacao de dados desnecessarios ou excessivos
          </li>
          <li>Solicitar portabilidade dos dados a outro fornecedor</li>
          <li>Solicitar eliminacao dos dados tratados com base no consentimento</li>
          <li>Obter informacoes sobre entidades com as quais compartilhamos dados</li>
          <li>Revogar o consentimento a qualquer momento</li>
        </ul>
        <p className="mt-2">
          Para exercer seus direitos, entre em contato: juridico@bossabox.com Responderemos
          sua solicitacao em ate 15 dias uteis.
        </p>
      </section>

      <section>
        <h3 className="font-medium text-foreground mb-2 text-sm">9. SEGURANCA DOS DADOS</h3>
        <p>
          Implementamos medidas tecnicas e organizacionais adequadas para proteger seus dados,
          incluindo:
        </p>
        <ul className="list-disc pl-5 mt-2 space-y-1">
          <li>Criptografia de dados em transito (HTTPS/TLS) e em repouso</li>
          <li>Controle de acesso restrito baseado em necessidade</li>
          <li>Monitoramento continuo de seguranca</li>
          <li>Backups regulares e plano de recuperacao de desastres</li>
          <li>Treinamento de equipe sobre protecao de dados</li>
        </ul>
      </section>

      <section>
        <h3 className="font-medium text-foreground mb-2 text-sm">
          10. COOKIES E TECNOLOGIAS SIMILARES
        </h3>
        <p>
          Utilizamos cookies essenciais para o funcionamento da plataforma e cookies analiticos para
          melhorar sua experiencia. Voce pode gerenciar as preferencias de cookies atraves das
          configuracoes do seu navegador, mas isso pode afetar a funcionalidade do servico.
        </p>
      </section>

      <section>
        <h3 className="font-medium text-foreground mb-2 text-sm">11. ALTERACOES DESTA POLITICA</h3>
        <p>
          Podemos atualizar esta politica periodicamente. Alteracoes significativas serao
          comunicadas por e-mail ou atraves de aviso destacado na plataforma, com antecedencia de 30
          dias antes de entrarem em vigor.
        </p>
      </section>

      <section>
        <h3 className="font-medium text-foreground mb-2 text-sm">12. CONTATO E RECLAMACOES</h3>
        <p>Para duvidas, solicitacoes ou reclamacoes sobre o tratamento dos seus dados:</p>
        <p className="mt-2">E-mail do Encarregado (DPO): juridico@bossabox.com</p>
        <p className="mt-2">
          Voce tambem tem o direito de apresentar reclamacao a Autoridade Nacional de Protecao de
          Dados (ANPD): www.gov.br/anpd
        </p>
      </section>
    </>
  );
}
